import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AppError } from '../api/request';
import { SUPPORTED_LOCALES, type LocaleCode } from '../config/i18n';
import Seo from './Seo';

export default function Register() {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const current = SUPPORTED_LOCALES.includes(i18n.language as LocaleCode) ? i18n.language : 'tr';
  const locale: LocaleCode = current === 'en' ? 'en' : 'tr';
  const bullets = t('auth.register.bullets', { returnObjects: true }) as string[];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!agreed) return;
    setError('');
    setSubmitting(true);
    try {
      await register(email, password, name);
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('auth.register.genericError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-wrap bg-[#1c1420] text-[#f5eef7] font-sans antialiased">
      <Seo
        title={`${t('auth.register.title')} | Loopcast`}
        description={t('auth.register.subtitle')}
        locale={locale}
        canonicalPath="/register"
        noindex
      />
      {/* Left brand panel */}
      <div className="flex flex-[1_1_460px] min-w-[min(100%,380px)] flex-col justify-between border-r border-[#2b1f33] p-10 sm:p-14">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-[#f5eef7] hover:opacity-90">
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-[#ff6f91] text-sm font-bold text-[#2b0e18]">
              L
            </span>
            <span className="text-lg font-bold tracking-tight">Loopcast</span>
          </Link>
          <div className="flex overflow-hidden rounded-md border border-[#3d2e46] text-xs font-semibold">
            {SUPPORTED_LOCALES.map((locale) => {
              const active = current === locale;
              return (
                <button
                  key={locale}
                  type="button"
                  onClick={() => void i18n.changeLanguage(locale)}
                  aria-pressed={active}
                  className={`px-2.5 py-1.5 uppercase transition-colors ${
                    active ? 'bg-[#ff6f91] text-[#2b0e18]' : 'bg-[#251b2c] text-[#c9b8d1] hover:text-[#f5eef7]'
                  }`}
                >
                  {locale}
                </button>
              );
            })}
          </div>
        </div>

        <div className="my-12 max-w-[420px]">
          <h1 className="m-0 mb-4 text-[clamp(1.6rem,3vw,2.125rem)] font-extrabold leading-tight tracking-tight">
            {t('auth.register.heroTitle')}
          </h1>
          <p className="mb-7 text-[15px] leading-relaxed text-[#c9b8d1]">{t('auth.register.heroSub')}</p>
          <ul className="flex flex-col gap-3.5">
            {(Array.isArray(bullets) ? bullets : []).map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-[#e6dbe9]">
                <Check className="mt-0.5 h-[18px] w-[18px] shrink-0 stroke-[2.5] text-[#ff6f91]" aria-hidden />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <blockquote className="max-w-[380px] rounded-xl border border-[#3d2e46] bg-[#221733] p-5">
          <p className="m-0 mb-3 text-[13.5px] leading-relaxed text-[#f5eef7]">
            “{t('auth.register.quote')}”
          </p>
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#ff9a6c] text-xs font-bold text-[#2b0e18]">
              B
            </span>
            <cite className="not-italic text-[12.5px] font-semibold text-[#c9b8d1]">
              {t('auth.register.quoteAuthor')}
            </cite>
          </div>
        </blockquote>
      </div>

      {/* Right form panel */}
      <div className="flex flex-[1_1_420px] min-w-[min(100%,320px)] items-center justify-center p-8 sm:p-10">
        <div className="w-full max-w-[400px]">
          <h2 className="m-0 mb-2 text-2xl font-extrabold tracking-tight">{t('auth.register.title')}</h2>
          <p className="mb-6 text-[13.5px] text-[#c9b8d1]">{t('auth.register.subtitle')}</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
            <div>
              <label htmlFor="register-name" className="mb-1.5 block text-[12.5px] font-semibold text-[#c9b8d1]">
                {t('auth.register.nameLabel')}
              </label>
              <input
                id="register-name"
                type="text"
                required
                autoComplete="name"
                placeholder={t('auth.register.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-[#3d2e46] bg-[#1c1420] px-3.5 py-2.5 text-sm text-[#f5eef7] placeholder:text-[#6b5a72] outline-none transition-colors focus:border-[#ff6f91]"
              />
            </div>
            <div>
              <label htmlFor="register-email" className="mb-1.5 block text-[12.5px] font-semibold text-[#c9b8d1]">
                {t('auth.email')}
              </label>
              <input
                id="register-email"
                type="email"
                required
                autoComplete="email"
                placeholder={t('auth.register.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-[#3d2e46] bg-[#1c1420] px-3.5 py-2.5 text-sm text-[#f5eef7] placeholder:text-[#6b5a72] outline-none transition-colors focus:border-[#ff6f91]"
              />
            </div>
            <div>
              <label htmlFor="register-password" className="mb-1.5 block text-[12.5px] font-semibold text-[#c9b8d1]">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder={t('auth.register.minLength')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-[#3d2e46] bg-[#1c1420] py-2.5 pl-3.5 pr-12 text-sm text-[#f5eef7] placeholder:text-[#6b5a72] outline-none transition-colors focus:border-[#ff6f91]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 px-1 text-xs font-semibold text-[#a8899f] hover:text-[#f5eef7]"
                >
                  {showPassword ? t('auth.register.hidePassword') : t('auth.register.showPassword')}
                </button>
              </div>
            </div>

            <label className="mt-1 flex cursor-pointer items-start gap-2.5 text-[12.5px] leading-snug text-[#c9b8d1]">
              <input
                type="checkbox"
                checked={agreed}
                onChange={() => setAgreed((v) => !v)}
                className="mt-0.5 accent-[#ff6f91]"
              />
              <span>
                {t('auth.register.termsPre')}
                {t('auth.register.termsPre') ? ' ' : ''}
                <a href="/terms" className="text-[#ff9a6c] hover:text-[#ffb088]">
                  {t('auth.register.termsLink')}
                </a>{' '}
                {t('auth.register.termsMid')}{' '}
                <a href="/privacy" className="text-[#ff9a6c] hover:text-[#ffb088]">
                  {t('auth.register.privacyLink')}
                </a>
                {t('auth.register.termsPost')}
              </span>
            </label>

            {error && <p className="text-sm text-[#ff8a9e]">{error}</p>}

            <button
              type="submit"
              disabled={submitting || !agreed}
              className={`mt-1 w-full rounded-lg border-none py-3 text-[14.5px] font-bold transition-colors disabled:cursor-not-allowed ${
                agreed
                  ? 'bg-[#ff6f91] text-[#2b0e18] hover:bg-[#ff8aa5]'
                  : 'bg-[#3d2e46] text-[#a8899f]'
              }`}
            >
              {submitting ? t('auth.register.submitting') : t('auth.register.submit')}
            </button>
          </form>

          <p className="mt-5 text-center text-[13px] text-[#c9b8d1]">
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className="font-medium text-[#ff9a6c] hover:text-[#ffb088]">
              {t('auth.register.loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
